'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronRight } from 'lucide-react';
import { ResponsiveSheet, SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import type { FormCampusOption } from '@church/nextjs-ui/website/forms';
import type {
  ClientForm,
  FinishPerson,
  FinishState,
  GatheringDoor,
  MedicalDraft,
} from '@/lib/types';
import { errorText, fetchFinish, saveFinish } from '@/lib/api';
import { asksQuestions, stillOwed } from '@/lib/finish';
import { waiverOf } from '@/lib/questions';
import { EventSheetBand } from './EventSheetBand';
import { Avatar } from './Avatar';
import { Questions } from './Questions';
import { SignStep, type WaiverGroup } from './Waivers';

/**
 * FINISHING A REGISTRATION STARTED AT THE DOOR
 * ============================================
 *
 * Staff register a walk-up and take the money in the events app; the person
 * scans a QR to `?invoice=<guid>&finish=1` and lands here. One screen of
 * questions per person, the waiver signed once for everybody, then done. No
 * sign-in: the invoice link is the key (apps/api lib/events/finish.ts).
 *
 * The same `Questions` and `SignStep` the registration sheet draws, so the
 * form looks and validates the same whichever way somebody came in.
 */

type Screen = { kind: 'person'; index: number } | { kind: 'sign' } | { kind: 'done' };

interface Props {
  apiBaseUrl: string;
  invoiceGuid: string;
  open: boolean;
  onClose: () => void;
  event: { image: string | null; title: string; start: string; end: string | null };
  /** The event's own form, for people whose registration type has none. */
  form: ClientForm | null;
  doorForms: Record<number, ClientForm>;
  doors: GatheringDoor[];
  campuses: FormCampusOption[];
}

export function FinishSheet({
  apiBaseUrl,
  invoiceGuid,
  open,
  onClose,
  event,
  form,
  doorForms,
  doors,
  campuses,
}: Props) {
  const [state, setState] = useState<FinishState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<number, Record<string, string>>>({});
  const [medical, setMedical] = useState<Record<number, MedicalDraft[]>>({});
  const [notes, setNotes] = useState<Record<number, string>>({});
  const [signer, setSigner] = useState('');
  const signerInput = useRef<HTMLInputElement>(null);
  // The waiver is there to read from the start; signing it needs a name.
  const named = signer.trim().length > 1;
  const [at, setAt] = useState(0);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    fetchFinish(apiBaseUrl, invoiceGuid, controller.signal)
      .then((s) => {
        setState(s);
        setAnswers(Object.fromEntries(s.people.map((p) => [p.eventParticipantId, p.answers])));
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setLoadError(errorText(err, 'We could not open that registration.'));
      });
    return () => controller.abort();
  }, [apiBaseUrl, invoiceGuid, open]);

  const people = state?.people ?? [];
  const formOf = (p: FinishPerson): ClientForm | null =>
    (p.formId ? doorForms[p.formId] : null) ??
    (form && (!p.formId || p.formId === form.id) ? form : null);
  const answersOf = (p: FinishPerson) => answers[p.eventParticipantId] ?? {};

  const waivers: WaiverGroup[] = useMemo(() => {
    const groups = new Map<number, WaiverGroup>();
    for (const p of people) {
      const fields = waiverOf(formOf(p));
      if (!fields) continue;
      const mine = answers[p.eventParticipantId] ?? {};
      const covers = { key: String(p.eventParticipantId), firstName: p.firstName, image: null };
      const had = groups.get(fields.doc.id);
      if (had) {
        had.covers.push(covers);
        continue;
      }
      const agreed = fields.agree ? Boolean((mine[fields.agree.id] ?? '').trim()) : true;
      const signature = fields.signature ? (mine[fields.signature.id] ?? '') : '';
      groups.set(fields.doc.id, {
        fields,
        covers: [covers],
        agreed,
        signature,
        signed: agreed && (!fields.signature || signature.trim().length > 1),
      });
    }
    return [...groups.values()];
    // formOf reads props that do not change while the sheet is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people, answers]);

  const screens: Screen[] = [
    ...people
      .map((p, index) => ({ p, index }))
      .filter(({ p }) => asksQuestions(formOf(p)))
      .map(({ index }) => ({ kind: 'person' as const, index })),
    ...(waivers.length ? [{ kind: 'sign' as const }] : []),
    { kind: 'done' as const },
  ];
  const screen = screens[Math.min(at, screens.length - 1)] ?? { kind: 'done' };
  const lastBeforeDone = screens.length - 2;

  const setAnswer = (ep: number) => (id: number, value: string) =>
    setAnswers((a) => ({ ...a, [ep]: { ...(a[ep] ?? {}), [id]: value } }));

  const signAll = (group: WaiverGroup, field: 'agree' | 'signature', value: string) => {
    const target = group.fields[field];
    if (!target) return;
    setAnswers((a) => {
      const next = { ...a };
      for (const c of group.covers) {
        const ep = Number(c.key);
        next[ep] = { ...(next[ep] ?? {}), [target.id]: value };
      }
      return next;
    });
  };

  const owed =
    screen.kind === 'person'
      ? stillOwed(formOf(people[screen.index]!), answersOf(people[screen.index]!))
      : screen.kind === 'sign'
        ? waivers.every((w) => w.signed)
          ? null
          : 'Needs a signature'
        : null;

  const save = async () => {
    setPending(true);
    setError(null);
    try {
      const res = await saveFinish(
        apiBaseUrl,
        invoiceGuid,
        people.map((p) => ({ eventParticipantId: p.eventParticipantId, answers: answersOf(p) }))
      );
      if (res.ok) setAt(screens.length - 1);
      else setError(res.error);
    } catch (err) {
      setError(errorText(err, 'We could not save that just now.'));
    } finally {
      setPending(false);
    }
  };

  const next = () => {
    if (owed) return;
    if (at >= lastBeforeDone) void save();
    else setAt(at + 1);
  };

  const doorLabel = (p: FinishPerson) => doors.find((d) => d.id === p.doorId)?.label ?? null;

  return (
    <ResponsiveSheet
      open={open}
      onClose={onClose}
      maxWidth="max-w-3xl"
      panelClassName="bg-ground text-ink md:rounded-3xl"
      noPanelPadding
      hideCloseButton
      header={
        <EventSheetBand
          image={event.image}
          title={event.title}
          start={event.start}
          end={event.end}
          onClose={onClose}
        />
      }
      footer={
        state && screen.kind !== 'done' ? (
          <div className="ed glass flex items-center gap-2 border-x-0 border-b-0 px-5 py-4">
            {at > 0 && (
              <button
                type="button"
                className="btn btn-quiet shrink-0"
                onClick={() => setAt(at - 1)}
                disabled={pending}
              >
                Back
              </button>
            )}
            <div className="ml-auto flex min-w-0 items-center gap-3">
              {owed && (
                <span className="text-slate max-w-[14rem] min-w-0 truncate text-xs font-semibold">
                  {owed}
                </span>
              )}
              <button
                type="button"
                className="btn btn-primary shrink-0"
                onClick={next}
                disabled={Boolean(owed) || pending}
              >
                {pending ? 'One moment…' : at >= lastBeforeDone ? 'Finish' : 'Continue'}
                {!pending && at < lastBeforeDone && <ChevronRight className="h-4 w-4" />}
              </button>
            </div>
          </div>
        ) : null
      }
    >
      <SheetPage name="main">
        <div className="ed min-h-[24rem] px-5 pt-2 pb-8">
          {loadError ? (
            <div className="bg-rose-soft text-rose rounded-2xl px-4 py-3 text-sm font-semibold">
              {loadError}
            </div>
          ) : !state ? (
            <div className="text-slate py-10 text-center text-sm">Loading…</div>
          ) : screen.kind === 'person' ? (
            (() => {
              const p = people[screen.index]!;
              const label = doorLabel(p);
              return (
                <section className="space-y-5">
                  <div className="flex items-center gap-3">
                    <Avatar name={p.name} image={null} size={44} />
                    <div className="min-w-0">
                      <p className="text-navy text-base font-bold">{p.name}</p>
                      {label && <p className="text-slate text-xs font-semibold">{label}</p>}
                    </div>
                  </div>
                  <Questions
                    form={formOf(p)}
                    answers={answersOf(p)}
                    setAnswer={setAnswer(p.eventParticipantId)}
                    campuses={campuses}
                    person={null}
                    household={[]}
                    medical={medical[p.eventParticipantId] ?? []}
                    setMedical={(rows) =>
                      setMedical((m) => ({ ...m, [p.eventParticipantId]: rows }))
                    }
                    medicalNote={notes[p.eventParticipantId] ?? ''}
                    setMedicalNote={(v) => setNotes((n) => ({ ...n, [p.eventParticipantId]: v }))}
                    registrantName={p.firstName || 'them'}
                    aboutName={p.firstName || null}
                  />
                </section>
              );
            })()
          ) : screen.kind === 'sign' ? (
            <div className="space-y-4">
              <label className="block">
                <span className="kicker">Your name</span>
                <input
                  ref={signerInput}
                  className="border-line bg-paper text-navy mt-1.5 w-full rounded-xl border px-3.5 py-2.5 text-sm"
                  value={signer}
                  onChange={(e) => setSigner(e.target.value)}
                  autoComplete="name"
                />
              </label>
              <SignStep
                waivers={waivers}
                signer={signer.trim()}
                onAgree={(g, yes) => {
                  if (yes && !named) return signerInput.current?.focus();
                  signAll(g, 'agree', yes ? (g.fields.agree?.options[0] ?? 'Yes') : '');
                }}
                onSign={(g, name) => {
                  if (name && !named) return signerInput.current?.focus();
                  signAll(g, 'signature', name);
                }}
              />
            </div>
          ) : (
            <div className="card p-6 text-center">
              <Check className="text-teal mx-auto h-8 w-8" />
              <h3 className="text-navy mt-3 text-lg font-extrabold">You are all set</h3>
              <p className="text-slate mt-1 text-sm">{people.map((p) => p.firstName).join(', ')}</p>
              <button type="button" className="btn btn-navy mt-4 w-full" onClick={onClose}>
                Done
              </button>
            </div>
          )}
          {error && (
            <div className="bg-rose-soft text-rose mt-4 rounded-2xl px-4 py-3 text-sm font-semibold">
              {error}
            </div>
          )}
        </div>
      </SheetPage>
    </ResponsiveSheet>
  );
}
